import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type WebSearchResult = {
  title: string;
  snippet: string;
  url: string;
};

type DuckDuckGoTopic = {
  Text?: string;
  FirstURL?: string;
  Topics?: DuckDuckGoTopic[];
};

type DuckDuckGoResponse = {
  Abstract?: string;
  AbstractSource?: string;
  AbstractURL?: string;
  Answer?: string;
  AnswerType?: string;
  RelatedTopics?: DuckDuckGoTopic[];
};

/**
 * Small, keyless web-search adapter. The API returns instant answers and related
 * sources; deployments can point `webSearchUrl` at a richer search gateway.
 */
@Injectable({ providedIn: 'root' })
export class WebSearchService {
  private readonly http = inject(HttpClient);

  async search(query: string, limit = 5): Promise<WebSearchResult[]> {
    try {
      const results = await this.searchResultPage(query, limit);
      if (results.length) return results;
    } catch {
      // The HTML endpoint can occasionally reject automated requests; use the
      // JSON instant-answer endpoint as a small but reliable fallback.
    }
    return this.searchInstantAnswers(query, limit);
  }

  private async searchResultPage(query: string, limit: number): Promise<WebSearchResult[]> {
    const params = new HttpParams()
      .set('q', query)
      .set('kl', 'us-en');
    const html = await firstValueFrom(
      this.http.get(environment.webSearchUrl, { params, responseType: 'text' }),
    );
    const document = new DOMParser().parseFromString(html, 'text/html');
    const resultElements = Array.from(document.querySelectorAll('.result, article[data-testid="result"]'));
    const results = resultElements.flatMap((element) => {
      const link = element.querySelector<HTMLAnchorElement>('.result__a, a.result-link, h2 a');
      if (!link?.textContent?.trim()) return [];
      const snippet = element.querySelector('.result__snippet, .result-snippet, [data-result="snippet"]')?.textContent?.trim() || '';
      return [{
        title: link.textContent.trim(),
        snippet,
        url: this.resultUrl(link.getAttribute('href') || ''),
      }];
    });
    return results.filter((result) => result.url || result.snippet).slice(0, limit);
  }

  private async searchInstantAnswers(query: string, limit: number): Promise<WebSearchResult[]> {
    const params = new HttpParams()
      .set('q', query)
      .set('format', 'json')
      .set('no_html', '1')
      .set('skip_disambig', '1');
    const response = await firstValueFrom(
      this.http.get<DuckDuckGoResponse>(environment.webInstantAnswerUrl, { params }),
    );

    const results: WebSearchResult[] = [];
    if (response.Abstract?.trim()) {
      results.push({
        title: response.AbstractSource?.trim() || 'Web result',
        snippet: response.Abstract.trim(),
        url: response.AbstractURL || '',
      });
    }
    if (response.Answer?.trim()) {
      results.push({
        title: response.AnswerType ? `Answer: ${response.AnswerType}` : 'Web answer',
        snippet: response.Answer.trim(),
        url: '',
      });
    }

    for (const topic of this.flattenTopics(response.RelatedTopics ?? [])) {
      if (!topic.Text?.trim()) continue;
      results.push({ title: this.titleFromTopic(topic.Text), snippet: topic.Text.trim(), url: topic.FirstURL || '' });
      if (results.length >= limit) break;
    }
    return results.slice(0, limit);
  }

  buildContext(results: WebSearchResult[]): string {
    return results.map((result) =>
      `SOURCE: ${result.title}${result.url ? ` (${result.url})` : ''}\n\n${result.snippet}`,
    ).join('\n\n---\n\n');
  }

  private flattenTopics(topics: DuckDuckGoTopic[]): DuckDuckGoTopic[] {
    return topics.flatMap((topic) => topic.Topics ? this.flattenTopics(topic.Topics) : [topic]);
  }

  private titleFromTopic(text: string): string {
    return text.split(' - ', 1)[0].trim() || 'Web result';
  }

  private resultUrl(href: string): string {
    if (!href) return '';
    try {
      const url = new URL(href, 'https://duckduckgo.com');
      return url.searchParams.get('uddg') || url.href;
    } catch {
      return href;
    }
  }
}
